import React, { useState, useEffect } from 'react';
import axios from 'axios';

const UserManagement = ({ currentUser }) => {
  const [activeSubSection, setActiveSubSection] = useState('users');
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [showPosModal, setShowPosModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [editingPos, setEditingPos] = useState(null);
  const [deleteConfirmOrg, setDeleteConfirmOrg] = useState(null);
  const [deleteConfirmPos, setDeleteConfirmPos] = useState(null);

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    organization_id: '',
    position_id: '',
    phone: '',
    telegram_chat_id: '',
    is_active: true,
    is_admin: false
  });

  const [orgFormData, setOrgFormData] = useState({
    mail_domain: '',
    name: '',
    full_name: '',
    inn: '',
    kpp: '',
    address: '',
    is_active: true
  });

  const [posFormData, setPosFormData] = useState({
    name: '',
    is_active: true
  });

  useEffect(() => {
    fetchUsers();
    fetchOrganizations();
    fetchPositions();
  }, []);

  useEffect(() => { const t = setTimeout(() => { setSuccess(''); setError(''); }, 5000); return () => clearTimeout(t); }, [success, error]);

  const fetchUsers = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setLoading(true);
    setError('');

    try {
      const response = await axios.get('/api/users/', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setUsers(response.data.users);
    } catch (error) {
      if (error.response && error.response.status === 403) {
        setError('У вас нет прав для управления пользователями.');
      } else {
        setError('Не удалось загрузить список пользователей. Пожалуйста, попробуйте снова.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await axios.get('/api/organizations/?limit=500', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrganizations(response.data.organizations || []);
    } catch (err) {
      if (err.response && err.response.status !== 403) {
        setOrganizations([]);
      }
    }
  };

  const fetchPositions = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await axios.get('/api/positions/?limit=500', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPositions(response.data.positions || []);
    } catch (err) {
      if (err.response && err.response.status !== 403) {
        setPositions([]);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) return;

    setError('');
    setSuccess('');

    const payload = {
      ...formData,
      organization_id: formData.organization_id ? parseInt(formData.organization_id, 10) : null,
      position_id: formData.position_id ? parseInt(formData.position_id, 10) : null
    };
    if (!payload.username || !payload.password && !editingUser) {
      setError('Логин и пароль обязательны при создании пользователя.');
      return;
    }
    if (!editingUser && !payload.password) {
      setError('Пароль обязателен при создании пользователя.');
      return;
    }

    try {
      if (editingUser) {
        const updateData = { ...payload };
        if (!updateData.password) delete updateData.password;
        await axios.put(`/api/users/${editingUser.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Пользователь успешно обновлен!');
      } else {
        await axios.post('/api/users/', payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Пользователь успешно создан!');
      }
      setShowModal(false);
      setEditingUser(null);
      resetForm();
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось сохранить пользователя.');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email || '',
      full_name: user.full_name || '',
      password: '',
      organization_id: user.organization_id || '',
      position_id: user.position_id || '',
      phone: user.phone || '',
      telegram_chat_id: user.telegram_chat_id || '',
      is_active: user.is_active,
      is_admin: user.is_admin
    });
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setError('');
    setSuccess('');

    try {
      await axios.delete(`/api/users/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setSuccess('Пользователь успешно удален!');
      setDeleteConfirm(null);
      fetchUsers();
    } catch (error) {
      if (error.response && error.response.data && error.response.data.detail) {
        setError(error.response.data.detail);
      } else {
        setError('Не удалось удалить пользователя. Пожалуйста, попробуйте снова.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      full_name: '',
      password: '',
      organization_id: '',
      position_id: '',
      phone: '',
      telegram_chat_id: '',
      is_active: true,
      is_admin: false
    });
  };

  const openCreateModal = () => {
    setEditingUser(null);
    resetForm();
    setShowModal(true);
  };

  const openOrgModal = (org) => {
    if (org) {
      setEditingOrg(org);
      setOrgFormData({
        name: org.name,
        full_name: org.full_name || '',
        inn: org.inn || '',
        kpp: org.kpp || '',
        address: org.address || '',
        mail_domain: org.mail_domain || '',
        is_active: org.is_active
      });
    } else {
      setEditingOrg(null);
      setOrgFormData({ name: '', full_name: '', inn: '', kpp: '', address: '', mail_domain: '', is_active: true });
    }
    setShowOrgModal(true);
  };

  const openPosModal = (pos) => {
    if (pos) {
      setEditingPos(pos);
      setPosFormData({ name: pos.name, is_active: pos.is_active });
    } else {
      setEditingPos(null);
      setPosFormData({ name: '', is_active: true });
    }
    setShowPosModal(true);
  };

  const handleOrgSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) return;
    setError('');
    try {
      if (editingOrg) {
        await axios.put(`/api/organizations/${editingOrg.id}`, orgFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Организация обновлена.');
      } else {
        const res = await axios.post('/api/organizations/', orgFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Организация создана.');
        if (showModal && res.data && res.data.id) {
          setFormData((prev) => ({ ...prev, organization_id: String(res.data.id) }));
        }
      }
      setShowOrgModal(false);
      fetchOrganizations();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось сохранить организацию.');
    }
  };

  const handlePosSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) return;
    setError('');
    try {
      if (editingPos) {
        await axios.put(`/api/positions/${editingPos.id}`, posFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Должность обновлена.');
      } else {
        const res = await axios.post('/api/positions/', posFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Должность создана.');
        if (showModal && res.data && res.data.id) {
          setFormData((prev) => ({ ...prev, position_id: String(res.data.id) }));
        }
      }
      setShowPosModal(false);
      fetchPositions();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось сохранить должность.');
    }
  };

  const handleDeleteOrg = async (id) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      await axios.delete(`/api/organizations/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess('Организация удалена.');
      setDeleteConfirmOrg(null);
      fetchOrganizations();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось удалить организацию.');
    }
  };

  const handleDeletePos = async (id) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      await axios.delete(`/api/positions/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess('Должность удалена.');
      setDeleteConfirmPos(null);
      fetchPositions();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось удалить должность.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Н/Д';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="user-management">
        <div className="content-header">
          <h1>Управление пользователями</h1>
          <p>Управление пользователями системы и правами доступа</p>
        </div>
        <div className="loading">Загрузка пользователей...</div>
      </div>
    );
  }

  if (error && error.includes('permission')) {
    return (
      <div className="user-management">
        <div className="content-header">
          <h1>Управление пользователями</h1>
          <p>Управление пользователями системы и правами доступа</p>
        </div>
        <div className="error">
          {error}
          <div style={{ marginTop: '10px' }}>
            Для доступа к этому разделу требуются права администратора.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="content-header">
        <div>
          <h1>Пользователи</h1>
          <p>Управление пользователями, организациями и должностями</p>
        </div>
        <div className="header-actions">
          {activeSubSection === 'users' && (
            <button className="btn-success btn-small" onClick={openCreateModal}>
              + Добавить пользователя
            </button>
          )}
          {activeSubSection === 'organizations' && (
            <button className="btn-success btn-small" onClick={() => openOrgModal(null)}>
              + Добавить организацию
            </button>
          )}
          {activeSubSection === 'positions' && (
            <button className="btn-success btn-small" onClick={() => openPosModal(null)}>
              + Добавить должность
            </button>
          )}
        </div>
      </div>

      <div className="subsection-tabs" style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <button
          type="button"
          className={`btn-small ${activeSubSection === 'users' ? 'btn-success' : 'btn-secondary'}`}
          onClick={() => setActiveSubSection('users')}
        >
          Пользователи
        </button>
        <button
          type="button"
          className={`btn-small ${activeSubSection === 'organizations' ? 'btn-success' : 'btn-secondary'}`}
          onClick={() => setActiveSubSection('organizations')}
        >
          Организации
        </button>
        <button
          type="button"
          className={`btn-small ${activeSubSection === 'positions' ? 'btn-success' : 'btn-secondary'}`}
          onClick={() => setActiveSubSection('positions')}
        >
          Должности
        </button>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      {activeSubSection === 'users' && (
        <>
          {users.length === 0 ? (
            <div className="no-data">
              Пользователи не найдены. Создайте первого пользователя, чтобы начать.
            </div>
          ) : (
            <table className="user-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Имя пользователя</th>
                  <th>Email</th>
                  <th>Полное имя</th>
                  <th>Организация</th>
                  <th>Должность</th>
                  <th>Телефон</th>
                  <th>Статус</th>
                  <th>Роль</th>
                  <th>Создан</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.id}</td>
                    <td>
                      <strong>{user.username}</strong>
                      {currentUser && currentUser.id === user.id && (
                        <div style={{ fontSize: '12px', color: '#fbbc05' }}>(Вы)</div>
                      )}
                    </td>
                    <td>{user.email || 'Н/Д'}</td>
                    <td>{user.full_name || 'Н/Д'}</td>
                    <td>{organizations.find((o) => o.id === user.organization_id)?.name || 'Н/Д'}</td>
                    <td>{positions.find((p) => p.id === user.position_id)?.name || 'Н/Д'}</td>
                    <td>{user.phone || 'Н/Д'}</td>
                    <td>
                      <span className={`badge ${user.is_active ? 'badge-active' : 'badge-inactive'}`}>
                        {user.is_active ? 'Активен' : 'Неактивен'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${user.is_admin ? 'badge-admin' : ''}`}>
                        {user.is_admin ? 'Админ' : 'Пользователь'}
                      </span>
                    </td>
                    <td>{formatDate(user.created_at)}</td>
                    <td>
                      <div className="user-actions">
                        <button 
                          className="btn-secondary btn-small"
                          onClick={() => handleEdit(user)}
                          disabled={!currentUser?.is_admin}
                        >
                          Редактировать
                        </button>
                        {currentUser?.is_admin && user.email && organizations.find(o => o.id === user.organization_id)?.mail_domain && (
                          <button 
                            className="btn-secondary btn-small"
                            onClick={async () => {
                              try {
                                await axios.post('/api/users/' + user.id + '/create-mailbox', {}, { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } });
                                setSuccess('Почтовый ящик создан');
                              } catch (e) { setError(e.response?.data?.detail || 'Ошибка'); }
                            }}
                            title="Создать почтовый ящик (требуется org.mail_domain)"
                          >
                            📧 Ящик
                          </button>
                        )}
                        <button 
                          className="btn-danger btn-small"
                          onClick={() => setDeleteConfirm(user)}
                          disabled={!currentUser?.is_admin || currentUser.id === user.id}
                        >
                          Удалить
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {activeSubSection === 'organizations' && (
        <>
          {organizations.length === 0 ? (
            <div className="no-data">
              Организации не найдены. Создайте первую организацию (юридическое лицо).
            </div>
          ) : (
            <table className="user-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Название</th>
                  <th>Полное наименование</th>
                  <th>ИНН</th>
                  <th>КПП</th>
                  <th>Адрес</th>
                  <th>Статус</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {organizations.map((org) => (
                  <tr key={org.id}>
                    <td>{org.id}</td>
                    <td><strong>{org.name}</strong></td>
                    <td>{org.full_name || '—'}</td>
                    <td>{org.inn || '—'}</td>
                    <td>{org.kpp || '—'}</td>
                    <td style={{ maxWidth: '200px' }}>{org.address || '—'}</td>
                    <td>
                      <span className={`badge ${org.is_active ? 'badge-active' : 'badge-inactive'}`}>
                        {org.is_active ? 'Активна' : 'Неактивна'}
                      </span>
                    </td>
                    <td>
                      <div className="user-actions">
                        <button className="btn-secondary btn-small" onClick={() => openOrgModal(org)}>Редактировать</button>
                        <button className="btn-danger btn-small" onClick={() => setDeleteConfirmOrg(org)}>Удалить</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {activeSubSection === 'positions' && (
        <>
          {positions.length === 0 ? (
            <div className="no-data">
              Должности не найдены. Создайте первую должность.
            </div>
          ) : (
            <table className="user-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Наименование должности</th>
                  <th>Статус</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((pos) => (
                  <tr key={pos.id}>
                    <td>{pos.id}</td>
                    <td><strong>{pos.name}</strong></td>
                    <td>
                      <span className={`badge ${pos.is_active ? 'badge-active' : 'badge-inactive'}`}>
                        {pos.is_active ? 'Активна' : 'Неактивна'}
                      </span>
                    </td>
                    <td>
                      <div className="user-actions">
                        <button className="btn-secondary btn-small" onClick={() => openPosModal(pos)}>Редактировать</button>
                        <button className="btn-danger btn-small" onClick={() => setDeleteConfirmPos(pos)}>Удалить</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{editingUser ? 'Редактировать пользователя' : 'Создать нового пользователя'}</h2>
            <p style={{ fontSize: '12px', color: '#888', marginBottom: '12px' }}>Обязательны только логин и пароль (пароль — при создании).</p>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Имя пользователя (логин) *</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  disabled={!!editingUser}
                  title="Уникальный логин для входа в систему. При редактировании изменить нельзя."
                />
              </div>
              <div className="form-group">
                <label>{editingUser ? 'Новый пароль (оставьте пустым, чтобы не менять)' : 'Пароль *'}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  placeholder={editingUser ? 'Оставьте пустым для сохранения текущего' : 'Введите пароль'}
                  title={editingUser ? 'Оставьте пустым, чтобы сохранить текущий пароль' : 'Задайте надёжный пароль для входа'}
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  title="Email для уведомлений и восстановления доступа"
                />
              </div>
              <div className="form-group">
                <label>Полное имя</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  title="ФИО или отображаемое имя пользователя"
                />
              </div>
              <div className="form-group">
                <label>Организация</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch', flexWrap: 'wrap' }}>
                  <select
                    value={formData.organization_id}
                    onChange={(e) => setFormData({ ...formData, organization_id: e.target.value })}
                    style={{ flex: '1 1 300px', minWidth: '0', maxWidth: '100%', width: '100%' }}
                    title={formData.organization_id ? (organizations.find((o) => o.id === parseInt(formData.organization_id, 10))?.full_name || organizations.find((o) => o.id === parseInt(formData.organization_id, 10))?.name) : 'Выберите организацию (юридическое лицо) пользователя'}
                  >
                    <option value="">— не выбрана —</option>
                    {organizations.filter((o) => o.is_active).map((o) => (
                      <option key={o.id} value={o.id} title={o.full_name || o.name}>
                        {o.full_name || o.name}
                      </option>
                    ))}
                  </select>
                  <button type="button" className="btn-secondary btn-small" onClick={() => openOrgModal(null)} style={{ flexShrink: 0 }}>
                    + Создать организацию
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Должность</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch', flexWrap: 'wrap' }}>
                  <select
                    value={formData.position_id}
                    onChange={(e) => setFormData({ ...formData, position_id: e.target.value })}
                    style={{ flex: '1 1 300px', minWidth: '0', maxWidth: '100%', width: '100%' }}
                    title={formData.position_id ? positions.find((p) => p.id === parseInt(formData.position_id, 10))?.name : 'Выберите должность пользователя'}
                  >
                    <option value="">— не выбрана —</option>
                    {positions.filter((p) => p.is_active).map((p) => (
                      <option key={p.id} value={p.id} title={p.name}>{p.name}</option>
                    ))}
                  </select>
                  <button type="button" className="btn-secondary btn-small" onClick={() => openPosModal(null)} style={{ flexShrink: 0 }}>
                    + Создать должность
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Телефон</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+7 ..."
                  title="Номер телефона в формате +7 XXX XXX XX XX"
                />
              </div>
              <div className="form-group">
                <label>Chat-id Telegram</label>
                <input
                  type="text"
                  value={formData.telegram_chat_id}
                  onChange={(e) => setFormData({ ...formData, telegram_chat_id: e.target.value })}
                  placeholder="Числовой ID чата Telegram"
                  title="Числовой ID чата для уведомлений. Узнать: @userinfobot в Telegram"
                />
              </div>
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                <label htmlFor="is_active">Активный пользователь</label>
              </div>
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="is_admin"
                  checked={formData.is_admin}
                  onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                />
                <label htmlFor="is_admin">Администратор</label>
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn-success">
                  {editingUser ? 'Обновить пользователя' : 'Создать пользователя'}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => { setShowModal(false); setEditingUser(null); resetForm(); }}
                >
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showOrgModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{editingOrg ? 'Редактировать организацию' : 'Новая организация (юридическое лицо)'}</h2>
            <form onSubmit={handleOrgSubmit}>
              <div className="form-group">
                <label>Название *</label>
                <input
                  type="text"
                  value={orgFormData.name}
                  onChange={(e) => setOrgFormData({ ...orgFormData, name: e.target.value })}
                  required
                  title="Краткое название организации"
                />
              </div>
              <div className="form-group">
                <label>Полное наименование</label>
                <input
                  type="text"
                  value={orgFormData.full_name}
                  onChange={(e) => setOrgFormData({ ...orgFormData, full_name: e.target.value })}
                  title="Полное юридическое наименование по учредительным документам"
                />
              </div>
              <div className="form-group">
                <label>ИНН</label>
                <input
                  type="text"
                  value={orgFormData.inn}
                  onChange={(e) => setOrgFormData({ ...orgFormData, inn: e.target.value })}
                  title="Идентификационный номер налогоплательщика (10 или 12 цифр)"
                />
              </div>
              <div className="form-group">
                <label>КПП</label>
                <input
                  type="text"
                  value={orgFormData.kpp}
                  onChange={(e) => setOrgFormData({ ...orgFormData, kpp: e.target.value })}
                  title="Код причины постановки на учёт (9 цифр)"
                />
              </div>
              <div className="form-group">
                <label>Адрес</label>
                <textarea
                  value={orgFormData.address}
                  onChange={(e) => setOrgFormData({ ...orgFormData, address: e.target.value })}
                  rows={2}
                  title="Юридический или почтовый адрес организации"
                />
              </div>
              <div className="form-group">
                <label>Почтовый домен</label>
                <input
                  type="text"
                  value={orgFormData.mail_domain}
                  onChange={(e) => setOrgFormData({ ...orgFormData, mail_domain: e.target.value })}
                  placeholder="example.com (ящики user@example.com)"
                  title="Домен почты: ящики будут user@example.com. Задаёт возможность создавать почтовые ящики для организации"
                />
              </div>
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="org_is_active"
                  checked={orgFormData.is_active}
                  onChange={(e) => setOrgFormData({ ...orgFormData, is_active: e.target.checked })}
                />
                <label htmlFor="org_is_active">Активна</label>
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn-success">{editingOrg ? 'Сохранить' : 'Создать'}</button>
                <button type="button" className="btn-secondary" onClick={() => setShowOrgModal(false)}>Отмена</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPosModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{editingPos ? 'Редактировать должность' : 'Новая должность'}</h2>
            <form onSubmit={handlePosSubmit}>
              <div className="form-group">
                <label>Наименование должности *</label>
                <input
                  type="text"
                  value={posFormData.name}
                  onChange={(e) => setPosFormData({ ...posFormData, name: e.target.value })}
                  required
                  title="Название должности (например: Менеджер проекта, Разработчик)"
                />
              </div>
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="pos_is_active"
                  checked={posFormData.is_active}
                  onChange={(e) => setPosFormData({ ...posFormData, is_active: e.target.checked })}
                />
                <label htmlFor="pos_is_active">Активна</label>
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn-success">{editingPos ? 'Сохранить' : 'Создать'}</button>
                <button type="button" className="btn-secondary" onClick={() => setShowPosModal(false)}>Отмена</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirmOrg && (
        <div className="modal-overlay">
          <div className="confirmation-dialog">
            <h3>Удалить организацию?</h3>
            <p>Вы уверены, что хотите удалить организацию &quot;{deleteConfirmOrg.name}&quot;?</p>
            <div className="confirmation-actions">
              <button className="btn-danger btn-small" onClick={() => handleDeleteOrg(deleteConfirmOrg.id)}>Удалить</button>
              <button className="btn-secondary btn-small" onClick={() => setDeleteConfirmOrg(null)}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirmPos && (
        <div className="modal-overlay">
          <div className="confirmation-dialog">
            <h3>Удалить должность?</h3>
            <p>Вы уверены, что хотите удалить должность &quot;{deleteConfirmPos.name}&quot;?</p>
            <div className="confirmation-actions">
              <button className="btn-danger btn-small" onClick={() => handleDeletePos(deleteConfirmPos.id)}>Удалить</button>
              <button className="btn-secondary btn-small" onClick={() => setDeleteConfirmPos(null)}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="confirmation-dialog">
            <h3>Подтверждение удаления</h3>
            <p>
              Вы уверены, что хотите удалить пользователя <strong>"{deleteConfirm.username}"</strong>?
              Это действие нельзя отменить.
            </p>
            <div className="confirmation-actions">
              <button 
                className="btn-danger btn-small"
                onClick={() => handleDelete(deleteConfirm.id)}
              >
                Удалить
              </button>
              <button 
                className="btn-secondary btn-small"
                onClick={() => setDeleteConfirm(null)}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
